import { useEffect, useRef, useState } from "react";
import type { Player, PlayerId } from "@sportsos/engine";
import { api, ApiRequestError } from "../api.ts";
import { BottomSheet } from "./BottomSheet.tsx";
import { avatarColor, initials, maskPhone } from "./shared.ts";
import { InviteQRSheet } from "./TeamInvite.tsx";

const MAX_RESULTS = 4;

type Tab = "invite" | "search" | "manual";

interface AddPlayerSheetProps {
  readonly teamId: string;
  readonly teamName: string;
  readonly existingPlayerIds: readonly PlayerId[];
  /** Names already in the squad, so the Manual tab can warn (never block) on a repeat. */
  readonly existingNames?: readonly string[];
  readonly onPlayerAdded: (player: Player) => void;
  readonly onClose: () => void;
  /**
   * False while the team exists only in this browser — a side named during match setup
   * carries a locally generated id until `publishMatch` creates it. Adding to a squad the
   * server has never heard of is a 404, so the sheet hands the player straight to
   * `onPlayerAdded` and setup persists the whole squad at publish.
   */
  readonly teamIsSaved?: boolean;
  /**
   * Save an unsaved team to the server and return its id. An invite belongs to a team the
   * server knows, so the QR for a side named during match setup needs this first. Without
   * it the Invite tab asked for an invite to a team that did not exist and showed only
   * "Could not generate invite".
   */
  readonly saveTeam?: () => Promise<string>;
  /**
   * A few words added to the "✓ Rohit added" confirmation, such as "on the bench". Asked
   * before `onPlayerAdded`, while the page still describes the squad without him.
   */
  readonly describeAdded?: (player: Player) => string | null;
}

/** How long "✓ Rohit added" stays up: long enough to read, gone before the next name is typed. */
export const ADDED_TOAST_MS = 2500;

export function AddPlayerSheet({
  teamId,
  teamName,
  existingPlayerIds,
  existingNames = [],
  onPlayerAdded,
  onClose,
  teamIsSaved = true,
  saveTeam,
  describeAdded,
}: AddPlayerSheetProps) {
  const [tab, setTab] = useState<Tab>("invite");
  const [showQR, setShowQR] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  /**
   * Every tab adds through here, so each addition is confirmed the same way. The sheet is low
   * enough that the squad stays visible behind it, and the page highlights the new row; the
   * confirmation says it in words for a scorer who is looking at the keyboard.
   */
  const announce = (player: Player) => {
    const note = describeAdded?.(player) ?? null;
    onPlayerAdded(player);
    setToast(`✓ ${player.displayName} added${note === null ? "" : ` · ${note}`}`);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), ADDED_TOAST_MS);
  };

  const openQR = async () => {
    if (teamIsSaved) { setShowQR(true); return; }
    if (saveTeam === undefined) { setSaveError("Save the team first to invite by QR"); return; }
    setSavingTeam(true);
    setSaveError(null);
    try {
      await saveTeam();
      setShowQR(true);
    } catch (err: unknown) {
      setSaveError(err instanceof Error && err.message !== "" ? err.message : "Could not save the team");
    } finally {
      setSavingTeam(false);
    }
  };

  // Rendered only once the team is saved, so `teamId` is the server's id by now.
  if (showQR && teamIsSaved) {
    return <InviteQRSheet teamId={teamId} teamName={teamName} onClose={() => setShowQR(false)} />;
  }

  return (
    <BottomSheet
      title={`Add to ${teamName}`}
      onClose={onClose}
      compact
      above={toast === null ? null : <div className="added-toast" role="status">{toast}</div>}
    >
      <div className="ds-segmented" role="tablist" style={{ width: "100%", marginBottom: "var(--ds-space-3)" }}>
        {(["invite", "search", "manual"] as const).map((t) => (
          <button
            key={t}
            className="ds-segmented__item"
            role="tab"
            aria-selected={tab === t ? true : undefined}
            onClick={() => setTab(t)}
            style={{ flex: 1 }}
          >
            {t === "invite" ? "Invite" : t === "search" ? "Search" : "Manual"}
          </button>
        ))}
      </div>

      {tab === "invite" && <InviteTab onShowQR={() => { void openQR(); }} saving={savingTeam} error={saveError} />}
      {tab === "search" && (
        <SearchTab
          existingPlayerIds={existingPlayerIds}
          teamId={teamId}
          teamIsSaved={teamIsSaved}
          onPlayerAdded={announce}
        />
      )}
      {tab === "manual" && (
        <ManualTab
          teamId={teamId}
          teamIsSaved={teamIsSaved}
          existingNames={existingNames}
          onPlayerAdded={announce}
        />
      )}
    </BottomSheet>
  );
}

/* ── Invite tab ──────────────────────────────────────────────────────── */

function InviteTab({ onShowQR, saving, error }: { onShowQR: () => void; saving: boolean; error: string | null }) {
  return (
    <div>
      <p className="ds-caption" style={{ marginBottom: "var(--ds-space-2)" }}>
        Players scan the QR code or tap the link to join your team
      </p>
      <button
        className="ds-btn ds-btn--primary ds-btn--block"
        disabled={saving}
        onClick={onShowQR}
      >
        {saving ? "Saving team..." : "Show QR code"}
      </button>
      {error && <p className="ds-hint ds-hint--error" role="alert" style={{ marginTop: "var(--ds-space-2)" }}>{error}</p>}
    </div>
  );
}

/* ── Search tab ──────────────────────────────────────────────────────── */

function SearchTab({
  existingPlayerIds,
  teamId,
  teamIsSaved,
  onPlayerAdded,
}: {
  existingPlayerIds: readonly PlayerId[];
  teamId: string;
  teamIsSaved: boolean;
  onPlayerAdded: (player: Player) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<readonly Player[]>([]);
  const [searching, setSearching] = useState(false);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const onQueryChange = (value: string) => {
    setQuery(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = value.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    timerRef.current = window.setTimeout(() => {
      api.searchPlayers(q, "global")
        .then(({ players }) => {
          setResults(
            players
              .filter((p) => !existingPlayerIds.includes(p.id))
              .slice(0, MAX_RESULTS),
          );
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
  };

  const handleAdd = async (player: Player) => {
    setError(null);
    try {
      // Before anything else: the row may be a registered account rather than a player,
      // and only a real player id can be put in a squad, named as keeper, or given a
      // batting position. Resolving here keeps that distinction out of the rest of the app.
      const real = await api.resolvePlayer(player.id);
      // A team that setup has not published yet has no server-side squad to add to; the
      // player goes into the local XI and is written when the match is published.
      if (teamIsSaved) await api.addTeamPlayers(teamId, [real.id]);
      setAdded((prev) => new Set(prev).add(player.id));
      onPlayerAdded(real);
    } catch {
      // Swallowing this is what made the bug look like a dead button: the request failed,
      // nothing was added, and the row sat there unchanged with no explanation.
      setError(`Could not add ${player.displayName}. Please try again.`);
    }
  };

  return (
    <div>
      <div className="ds-field">
        <input
          className="ds-input"
          id="search-player"
          aria-label="Search by name, phone, or email"
          type="text"
          placeholder="e.g. Rohit, +91987..., rohit@..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          autoComplete="off"
          autoFocus
        />
      </div>

      {searching && query.trim().length >= 2 && (
        <p className="ds-caption" style={{ marginTop: "var(--ds-space-3)", textAlign: "center" }}>
          Searching...
        </p>
      )}

      {error && (
        <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>
          {error}
        </p>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: "var(--ds-space-3)" }}>
          <div className="ds-list">
            {results.map((player) => {
              const isAdded = added.has(player.id);
              const color = avatarColor(player.displayName);
              const meta = player.phone ? maskPhone(player.phone) : player.email ?? "";
              return (
                <button
                  className="ds-list-item"
                  key={player.id}
                  onClick={() => !isAdded && handleAdd(player)}
                  disabled={isAdded}
                  style={{ opacity: isAdded ? 0.6 : 1 }}
                >
                  <span className={`ds-avatar ds-avatar--sm ds-avatar--${color}`}>
                    {initials(player.displayName)}
                  </span>
                  <span className="ds-list-item__body">
                    <b className="ds-list-item__title">{player.displayName}</b>
                    {meta && <span className="ds-list-item__meta">{meta}</span>}
                  </span>
                  {isAdded ? (
                    <span className="ds-badge ds-badge--success">Added</span>
                  ) : (
                    <span className="ds-badge ds-badge--accent">Add</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {query.trim().length >= 2 && !searching && results.length === 0 && (
        <div style={{ textAlign: "center", padding: "var(--ds-space-5) 0" }}>
          <p className="ds-caption">No players found for "{query.trim()}"</p>
          <p className="ds-caption" style={{ marginTop: "var(--ds-space-1)" }}>
            Try a different name, phone, or email
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Manual tab ──────────────────────────────────────────────────────── */

function ManualTab({
  teamId,
  teamIsSaved,
  existingNames,
  onPlayerAdded,
}: {
  teamId: string;
  teamIsSaved: boolean;
  existingNames: readonly string[];
  onPlayerAdded: (player: Player) => void;
}) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const isPhone = /^\+?\d{7,15}$/.test(contact.replace(/\s/g, ""));
  const isEmail = contact.includes("@") && contact.length > 3;
  // A name alone is a guest player (CONTEXT.md, PRD ID-3). Contact is optional, but a
  // value that is typed in must still be a real phone or email.
  const hasContact = contact.trim() !== "";
  const contactValid = !hasContact || isPhone || isEmail;
  const sameName = name.trim() !== "" &&
    existingNames.some((n) => n.trim().toLowerCase() === name.trim().toLowerCase());

  const handleSave = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    if (!contactValid) { setError("Enter a valid phone number or email, or leave it blank"); return; }
    setSaving(true);
    setError(null);
    try {
      const player = await api.createPlayer({
        displayName: name.trim(),
        phone: isPhone ? contact.replace(/\s/g, "") : null,
        email: isEmail ? contact.trim().toLowerCase() : null,
      });
      // Creating the player is only half of "add player". Without this the row existed
      // but joined no squad, so the name appeared in the list from local state and was
      // gone on the next load — the team it was added to never referenced it.
      if (teamIsSaved) await api.addTeamPlayers(teamId, [player.id]);
      onPlayerAdded(player);
      // Ready for the next name at once: a scorer entering a squad types eleven in a row.
      setName("");
      setContact("");
      nameRef.current?.focus();
    } catch (err: unknown) {
      setError(
        err instanceof ApiRequestError && err.status === 409 ? err.message : "Could not add player",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!saving) void handleSave(); }}
      // Enter always gets an answer. A browser ignores Enter while the submit button is disabled,
      // so an empty name or a bad contact did nothing at all, which on a phone keyboard reads as
      // a dead key. Handled here, it says what is wrong instead.
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        if (!saving) void handleSave();
      }}
    >
      <div style={{ display: "flex", gap: "var(--ds-space-2)" }}>
        <input
          ref={nameRef}
          className="ds-input"
          id="manual-name"
          aria-label="Player name"
          type="text"
          placeholder="Player name"
          value={name}
          style={{ flex: 1, minWidth: 0 }}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          className="ds-btn ds-btn--primary"
          style={{ flexShrink: 0 }}
          disabled={saving || !name.trim() || !contactValid}
        >
          {saving ? "Adding..." : "Add"}
        </button>
      </div>
      {sameName && (
        <p className="ds-hint" role="status">
          Someone named {name.trim()} is already in this team. You can still add them if it is a different person.
        </p>
      )}

      <input
        className="ds-input"
        id="manual-contact"
        aria-label="Phone number or email (optional)"
        type="text"
        placeholder="Phone or email (optional)"
        value={contact}
        style={{ marginTop: "var(--ds-space-2)" }}
        onChange={(e) => setContact(e.target.value)}
      />
      {!contactValid ? (
        <p className="ds-hint ds-hint--error">Enter a valid phone (+91...) or email</p>
      ) : null}

      {error && <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{error}</p>}
    </form>
  );
}
